import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ReactComponent as ClockV2Icon } from '../../../assets/icons/clock-v2-icon.svg';
import { useAppBranding } from '../../../config/branding';
import DefaultApiLimit from '../../../config/defaultLimit';
import { PenaltyTransactionInterFace } from '../../../interface/penalty-transaction-interface';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import { setNotificationMessage } from '../../../redux/slices/NotificationManagementSlice';
import {
  fetchPenaltyTransaction,
  fetchPenaltyTransactionFilters,
  setSortDate,
  updatePenaltyTransactionFilters,
} from '../../../redux/slices/PenaltyTransactionSlice';
import CustomTable from '../../../shared-components/custom-table/CustomTable';
import TransactionSidePanel from '../../../shared-components/table-side-panel/TransactionSidePanel';
import { GetParametersFromCommonFilters } from '../../../shared-functions/CommonFilters';
import ConvertUTCtoDate from '../../../shared-functions/ConvertUTCtoDate';
import ConvertUTCtoHHMM from '../../../shared-functions/ConvertUTCtoHHMM';
import { exportCSV, exportPDF } from '../../../shared-functions/ExportFile';
import penaltyApi from '../../../utils/api/penalty-management';

/**
 * Renders the Transactions table for Penalty Management
 */
function TransactionList() {
  const branding = useAppBranding();
  const { t } = useTranslation('penaltyManagement');
  const [exportLoader, setExportLoader] = useState<boolean>(false);
  const [hiddenColumns, setHiddenColumns] = useState<string[]>([]);
  const dispatch = useAppDispatch();
  const {
    penaltyTransaction,
    limit,
    page,
    totalPages,
    totalResults,
    loading,
    sortBy,
    sortField,
    filters,
    filterListLoading,
    isFilterUpdated,
  } = useAppSelector((state) => state.transactionPenalty);

  /**
   * Fetch Users list function
   * @param {any} data - The data
   */
  const fetchData = (data: any) => {
    if (!loading) {
      dispatch(fetchPenaltyTransaction({ ...data }));
    }
  };
  const column = [
    {
      label: t('accountId'),
      accessorKey: 'accountCustomId' as keyof PenaltyTransactionInterFace,
    },
    {
      label: t('accountName'),
      accessorKey: 'accountName' as keyof PenaltyTransactionInterFace,
      sortAllow: true,
    },
    {
      label: t('transactionId'),
      accessorKey: 'transactionId' as keyof PenaltyTransactionInterFace,
      queryParamsKey: 'transactionObjectId',
      queryParamsValue: 'chargingTransactionObjectId',
      sidePanelComponent: <TransactionSidePanel fetchIdFrom="queryParams" />,
    },
    {
      label: t('transactionDate'),
      accessorKey: 'createdAt' as keyof PenaltyTransactionInterFace,
      sortAllow: true,
      /**
       * Data custom function
       * @param {any} currentRow - Current row data
       */
      customData: (currentRow: PenaltyTransactionInterFace) => (
        <div>{ConvertUTCtoDate(currentRow.createdAt)}</div>
      ),
    },
    {
      label: t('transactionStart'),
      accessorKey: 'penaltyTransactionStartTime' as keyof PenaltyTransactionInterFace,
      /**
       * Data custom function
       * @param {any} currentRow - Current row data
       */
      customData: (currentRow: PenaltyTransactionInterFace) => (
        <div>{ConvertUTCtoHHMM(currentRow.penaltyTransactionStartTime)}</div>
      ),
    },
    {
      label: t('transactionEnd'),
      accessorKey: 'penaltyTransactionEndTime' as keyof PenaltyTransactionInterFace, // This was creating a duplicate key issue if same accessorKey was used. Wait, earlier code had penaltyTransactionStartTime here too?
      /**
       * Data custom function
       * @param {any} currentRow - Current row data
       */
      customData: (currentRow: PenaltyTransactionInterFace) => (
        <div>{ConvertUTCtoHHMM(currentRow.penaltyTransactionEndTime)}</div>
      ),
    },

    {
      label: t('dynamicPricing'),
      accessorKey: 'dynamicPricing' as keyof PenaltyTransactionInterFace,
      sortAllow: true,
      /**
       * Data custom function
       * @param {any} currentRow - Current row data
       */
      customData: (currentRow: PenaltyTransactionInterFace) => (
        <div className="value-container">
          <span>{currentRow.dynamicPricingType}</span>
          <p className="value">{currentRow.dynamicPricing}</p>
          <span>{branding.text.currency}</span>
        </div>
      ),
    },
    {
      label: t('graceInterval'),
      accessorKey: 'graceTime' as keyof PenaltyTransactionInterFace,
      sortAllow: true,
      /**
       * Data custom function
       * @param {any} currentRow - Current row data
       */
      customData: (currentRow: PenaltyTransactionInterFace) => (
        <div>{currentRow.graceTime} Mins</div>
      ),
    },
    {
      label: t('idleCharge'),
      accessorKey: 'durationTime' as keyof PenaltyTransactionInterFace,
      sortAllow: true,
      /**
       * Data custom function
       * @param {any} currentRow - Current row data
       */
      customData: (currentRow: PenaltyTransactionInterFace) => (
        <div>{currentRow.durationTime} Mins</div>
      ),
    },
    {
      label: t('duration'),
      accessorKey: 'totalduration' as keyof PenaltyTransactionInterFace,
      sortAllow: true,
      /**
       * Data custom function
       * @param {any} currentRow - Current row data
       */
      customData: (currentRow: PenaltyTransactionInterFace) => {
        const status = currentRow.status?.toLowerCase();

        if (status === 'inprogress') {
          const duration = currentRow.totalduration;

          return (
            <div className="tag-duration">
              <div>{duration}</div>
              <div>
                <ClockV2Icon />
              </div>
            </div>
          );
        }

        if (status === 'completed') {
          const duration = currentRow.totalduration;

          return <div>{duration}</div>;
        }

        return <div>---</div>;
      },
    },
    {
      label: t('status'),
      accessorKey: 'status' as keyof PenaltyTransactionInterFace,
      /**
       * Data custom function
       * @param {any} currentRow - Current row data
       */
      customData: (currentRow: PenaltyTransactionInterFace) => {
        let output = <div />;
        switch (currentRow.status) {
          case 'inProgress':
            output = <div className="tag-status progress">{t('inProgress')} </div>;
            break;
          case 'completed':
            output = <div className="tag-status completed">{t('completed')} </div>;
            break;
          default:
            break;
        }

        return output;
      },
    },
    {
      label: t('penaltyAmountPerTransaction'),
      accessorKey: 'penaltyAmount' as keyof PenaltyTransactionInterFace,
      sortAllow: true,
      /**
       * Data custom function
       * @param {any} currentRow - Current row data
       */
      customData: (currentRow: PenaltyTransactionInterFace) => (
        <div className="value-container">
          <p className="value">{currentRow.penaltyAmount}</p>
          <span>{branding.text.currency}</span>
        </div>
      ),
    },
  ];

  /**
   * Column definitions for the table
   */
  const currentColumns = [
    { label: t('sNo'), accessorKey: '__serial__' },

    { label: t('accountId'), accessorKey: 'accountCustomId' },
    { label: t('accountName'), accessorKey: 'accountName' },

    { label: t('transactionId'), accessorKey: 'chargingTransactionObjectId' },
    { label: t('transactionDate'), accessorKey: 'createdAt' },

    { label: t('transactionStart'), accessorKey: 'penaltyTransactionStartTime' },
    { label: t('transactionEnd'), accessorKey: 'penaltyTransactionEndTime' },

    { label: t('dynamicPricingType'), accessorKey: 'dynamicPricingType' },
    { label: t('dynamicPricing'), accessorKey: 'dynamicPricing' },

    { label: t('graceInterval'), accessorKey: 'graceTime' },
    { label: t('idleCharge'), accessorKey: 'durationTime' },

    { label: t('status'), accessorKey: 'status' },
    { label: t('penaltyAmountPerTransaction'), accessorKey: 'penaltyAmount' },
  ];

  /**
   * Filtering columns for export without hidden columns
   */
  const exportCurrentColumns = currentColumns.filter(
    (col) => col.accessorKey === '__serial__' || !hiddenColumns.includes(col.accessorKey),
  );

  /**
   * Export data function
   * @param {any} type - The type
   */
  const exportDataPdfOrCSV = (type: string) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    const payload = {
      ...queryParameters,
    };
    if (sortBy) payload.sortBy = sortBy;
    if (sortField) payload.sortField = sortField;
    setExportLoader(true);
    penaltyApi
      .fetchPenaltyTransactionExport(payload)
      .then((response) => {
        // Convert data to CSV format
        const headers = exportCurrentColumns.map((col) => col.label);
        const rows = response.data.map((row: PenaltyTransactionInterFace, index: number) =>
          exportCurrentColumns.map((col) => {
            switch (col.accessorKey) {
              case '__serial__':
                return index + 1;

              case 'createdAt':
                return row.createdAt ? ConvertUTCtoDate(row.createdAt) : 'N/A';

              case 'penaltyTransactionStartTime':
                return row.penaltyTransactionStartTime
                  ? ConvertUTCtoHHMM(row.penaltyTransactionStartTime)
                  : 'N/A';

              case 'penaltyTransactionEndTime':
                return row.penaltyTransactionEndTime
                  ? ConvertUTCtoHHMM(row.penaltyTransactionEndTime)
                  : 'N/A';

              case 'dynamicPricing':
                return row.dynamicPricing ? `${row.dynamicPricing} ${branding.text.currency}` : '0';

              case 'penaltyAmount':
                return row.penaltyAmount ? `${row.penaltyAmount} ${branding.text.currency}` : '0';

              default:
                return (row as any)[col.accessorKey] ?? 'N/A';
            }
          }),
        );

        setExportLoader(false);
        if (rows.length > 0) {
          if (type === 'csv') {
            exportCSV(headers, rows, 'penalty-transaction-list.csv');
          } else {
            exportPDF(headers, rows, 'penalty-transaction-list.pdf');
          }
        }
      })
      .catch((error) => {
        setExportLoader(false);
        dispatch(
          setNotificationMessage({
            message: error?.response?.data?.message,
            status: true,
            type: 'error',
            code: error?.response?.data?.code,
          }),
        );
      });
  };
  /**
   * Table pagination function
   * @param {any} value - Pagination value
   */
  const pageNateLimitChange = (value: any) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    fetchData({
      ...value,
      ...queryParameters,
      sortBy,
      sortField,
    });
  };

  /**
   * Handle the filter data clear function
   * @param {any} sortData - The sortData
   */
  const handleSortFunction = (sortData: any) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    dispatch(setSortDate(sortData));
    fetchData({
      ...queryParameters,
      sortBy: sortData.sortBy,
      sortField: sortData.sortField,
      limit,
      page: DefaultApiLimit.page,
    });
  };

  /**
   * Function to refresh data
   */
  const handleRefresh = () => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    fetchData({
      page,
      limit,
      ...queryParameters,
      sortBy,
      sortField,
    });
  };

  useEffect(() => {
    if (isFilterUpdated || penaltyTransaction) {
      const queryParameters = GetParametersFromCommonFilters(filters);
      fetchData({
        page: DefaultApiLimit.page,
        limit,
        ...queryParameters,
      });
    }
  }, [filters]);

  return (
    <div>
      <CustomTable
        version="v2"
        header={column}
        data={penaltyTransaction}
        limit={limit}
        page={page}
        totalPages={totalPages}
        totalResults={totalResults}
        loading={loading}
        serialNo
        sortData={{ sortBy, sortField }}
        pageLimitChange={pageNateLimitChange}
        onHiddenColumnsChange={setHiddenColumns}
        handleSortChange={handleSortFunction}
        filters={filters}
        actionButton
        height={window.innerHeight >= 700 ? 347 : 349}
        filtersToDispatch={updatePenaltyTransactionFilters}
        filterListLoading={filterListLoading}
        filterListToDispatch={fetchPenaltyTransactionFilters}
        exportDataPdfOrCSV={exportDataPdfOrCSV}
        exportData={penaltyTransaction}
        exportLoader={exportLoader}
        refershCurrentPages={handleRefresh}
        sizeChangeButtonRequired
        leftTopContent={<div> </div>}
        rightTopContent={<div> </div>}
      />
    </div>
  );
}
export default TransactionList;
